import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';

// Modes live in a Meteor-free module so the bot process and unit tests can
// import them without Mongo; re-exported here so existing imports keep working.
export { botModes, canonicalBotMode, type BotMode } from '/imports/api/botModes';
import type { BotMode } from '/imports/api/botModes';

export type BotStatus =
  | 'offline' | 'connecting' | 'loggingIn' | 'running' | 'inTrade' | 'stalled' | 'restarting' | 'crashed';

export const botStatuses: BotStatus[] =
  ['offline', 'connecting', 'loggingIn', 'running', 'inTrade', 'stalled', 'restarting', 'crashed'];

/** Statuses that mean a bot is up (or on its way up). Shared, because three
 *  callers must agree on it: listRunningBots reports them, Start All skips
 *  them, and Stop All targets them. */
export const operationalStatuses: BotStatus[] =
  ['connecting', 'loggingIn', 'running', 'inTrade', 'stalled', 'restarting'];

export type StoreType =
  | 'redeemable' | 'nonredeemable' | 'pauper' | 'common' | 'rare' | 'booster' | 'legacy' | 'foil' | 'promo';

export type PricingSource = 'man' | 'gb';

export interface BotConfig {
  hoard: boolean;
  storetype: StoreType;
  shelfsize: number;
  stocksize: number;
  selllimit: number;
  /**
   * Buy on credit: keep buying past the bot's ticket balance and settle the
   * shortfall as store credit, instead of stopping when the tickets run out.
   *
   * Tri-state, and deliberately NOT in defaultConfig: unset (or null) inherits
   * fleet.overdraft, then Settings.overdraft, while `false` is a real answer
   * that overrides both. A plain boolean default here would be indistinguishable
   * from an operator's "no" and would make the inherited levels unreachable.
   */
  overdraft?: boolean | null;
  replenish: boolean;
  depleteTix: boolean;
  depleteCards: boolean;
  mintix: number;
  scrape: boolean;
  silent: boolean;
  bulk: boolean;
  pricing: PricingSource[];
  classified: string;
  /** Per-bot replenish priority override (higher = replenishes first).
   *  Unset inherits fleet.replenishPriority, then Settings, then 2. */
  replenishPriority?: number;
  /** Floor buy price for cards with no real buy price. Unset inherits
   *  fleet.defaultBuyPrice, then Settings. Junk buys go last in the cart. */
  defaultBuyPrice?: number | null;
  /** Floor sell price for junk -- cards with NEITHER a real buy nor sell
   *  price. Unset inherits fleet.defaultSellPrice, then Settings. */
  defaultSellPrice?: number | null;
  /**
   * Per-bot card selection overrides. When set (non-empty object), each
   * COMPLETELY REPLACES the fleet/Settings card list for this bot only -- no
   * merging, and later edits to shared lists do not affect this bot. {} and
   * junk count as unset. classifiedQuery composes with the `classified`
   * template above: the template controls ad FORMAT, this controls which
   * cards fill it.
   */
  classifiedQuery?: Record<string, unknown> | null;
  buyQuery?: Record<string, unknown> | null;
  sellQuery?: Record<string, unknown> | null;
  /** Inventory policy REF override (unlike the inline query overrides above):
   *  per-card stock quantities for this bot only. Set -> replaces the fleet's
   *  policy; unset -> inherits fleet.inventoryPolicyId. */
  inventoryPolicyId?: string | null;
  classifiedFile?: string;
  adjBuyPrice: number;
  adjSellPrice: number;
  expensivetaste: boolean;
  lifespan: number;
  parseUsers: boolean;
  advertisefoils: boolean;
  // When false, the bot neither joins the marketplace nor posts a classified;
  // it is "hidden" but still trades normally with anyone who opens a trade by
  // username. Missing (older configs) means true via defaultConfig.
  advertise: boolean;
  log?: boolean;
  // Eager modes only (eagerBuy/eagerSell/eagerHybrid): pacing for the trades
  // the bot INITIATES. Optional with code-side defaults; inert for every
  // other mode.
  eagerInvitesPerHour?: number;
  eagerCooldownHours?: number;
  /** Eager modes: court ONLY these chain families -- case-insensitive name
   *  prefixes, as in the Settings "Preferred B2B Chains" ('GoatBotsBoosters'
   *  matches GoatBotsBoosters, GoatBotsBoosters2...). This bot's marketplace
   *  pool is narrowed to them before the pick; group cycling and the
   *  fleet-wide preferred-only gate do not apply to it, the operator aimed
   *  this one account by hand. Absent or empty = the full pool. */
  eagerTargetFamilies?: string[];
  /** Eager modes: at least this many minutes between this bot's invitations,
   *  drawn fresh after each one between 50% and 150% of the value so the
   *  spacing is irregular (the 2-minute tick otherwise spends the hourly
   *  budget in one top-of-the-hour burst). Absent or 0 = the tick's cadence. */
  eagerMinGapMinutes?: number;
  /** Mechanics only: every chat line is dropped except the forced ones the
   *  trade protocol needs ('done', keymap digits) -- on every trade, inbound
   *  included. For a hidden account that must not read as a bot. Absent = off. */
  mechanicsOnly?: boolean;
  /** Eager modes: a random pause of this many ms before the bot answers a
   *  partner's prompt (judges, submits, chats). Humans take seconds; the
   *  controller otherwise answers 2.5 s after the cart settles. Absent = none. */
  eagerReplyMinMs?: number;
  eagerReplyMaxMs?: number;
  /** Probe: a chain's block heard by ANOTHER of our accounts does not stop
   *  this bot inviting it; only a block this bot heard itself does. For an
   *  account whose job is to find out whether a chain blocks per account
   *  or per bot. Absent = the fleet-wide mark applies, as for every bot. */
  probeChainBlocks?: boolean;
  /**
   * Engineered trades: negotiate eager-initiated trades to completion using
   * the partner's chatted prices, corrections and keymaps. Per-bot opt-in,
   * absent = off, so existing bots are untouched; MOGBOT_ENGINEERED=off is
   * the fleet-wide kill switch above it. Inert outside eager-initiated
   * trades by construction.
   */
  engineered?: boolean;
  /** Engineered selling floor tunables. Epsilon is a rounding buffer over
   *  actual acquisition cost (~1.01, NOT a profit target -- the profit comes
   *  from the cost + Goatbots-bid floor itself); minProfit is the absolute
   *  minimum basket profit in tix worth spending a trade slot on. Unset
   *  inherits the ENGINEERED_* constants / env. */
  engineeredEpsilon?: number;
  engineeredMinProfit?: number;
}

// Runtime defaults for BotConfig. Lives here (not server-only) so the admin
// bot form can pre-fill effective values for configs that predate a field; the
// bot merges these under its stored config ({ ...defaultConfig, ...config }).
export const defaultConfig: BotConfig = {
  hoard: false,
  storetype: 'redeemable',
  shelfsize: 4,
  stocksize: 8,
  selllimit: 4,
  replenish: true,
  depleteTix: true,
  depleteCards: true,
  // Tickets a bot keeps back from the banker. 0: deposit every ticket and let
  // the pre-trade replenish pull what a trade needs (the operator's choice,
  // 2026-10-06); a bot that must hold some sets its own.
  mintix: 0,
  scrape: false,
  silent: true,
  bulk: false,
  pricing: ['man', 'gb'],
  classified: '',
  adjBuyPrice: 0,
  adjSellPrice: 0,
  expensivetaste: true,
  lifespan: 6,
  parseUsers: false,
  advertisefoils: false,
  advertise: true,
};

export interface Bot extends Doc {
  _id: string;
  username: string;
  password: string;
  mfakey: string | null;
  uid: number | null;
  mode: BotMode;
  active?: boolean;
  intrade?: boolean;
  status?: BotStatus;
  /**
   * Operator intent: should this bot be running? Written ONLY by the
   * operator-facing methods (start/stop/startAll/stopAll/cancelStartAll);
   * the runner, watchdog and bot code never write it -- they READ it, at the
   * moment they act. `status` is the reverse: machinery-written, operator-
   * read. The two disagreeing is a bot in transition (or a fault), never
   * corruption. Absent (pre-migration doc) reads as 'down'.
   */
  desiredState?: 'up' | 'down';
  /** Stamped ~every 30s for each bot live in the botrunner. An operational
   *  status with a stale lastSeenAt is a claim, not an observation. */
  lastSeenAt?: Date;
  config: Partial<BotConfig>;
}

type Model = Bot;
type Refs = References<Model>;

export const Bots = new Mongo.Collection<Bot>('bots');

export const listBots = DeclareMethod<ListMethodArg<Bot>, ListMethodResponse<Bot, Refs>>('listBots');
export const getBot = DeclareMethod<string, GetMethodResponse<Bot, Refs>>('getBot');
export const createBot = DeclareMethod<Partial<Bot>, string>('createBot');
export const updateBot = DeclareMethod<Partial<Bot>, string>('updateBot');
export const deleteBot = DeclareMethod<string, undefined>('deleteBot');

export const startBot = DeclareMethod<{ username: string; mode?: BotMode; verbosity?: number }, { jobId: string }>('startBot');
export const stopBot = DeclareMethod<{ username: string }, { stopped: boolean }>('stopBot');
export const listRunningBots = DeclareMethod<void, string[]>('listRunningBots');

/** Fleet-wide start/stop. Both return immediately with the number of bots the
 *  job will work through; the job itself paces them and the UI follows along
 *  through the usual reactive status updates. */
export const startAllBots = DeclareMethod<void, { jobId: string; count: number }>('startAllBots');
export const stopAllBots = DeclareMethod<void, { jobId: string; count: number }>('stopAllBots');

/** Selection start/stop: the same server jobs as the fleet-wide pair, scoped
 *  to the named bots -- so pacing, ordering and desired-state writes live in
 *  one place and survive the operator closing the browser tab. */
export const startBots = DeclareMethod<{ usernames: string[] }, { jobId: string; count: number }>('startBots');
export const stopBots = DeclareMethod<{ usernames: string[] }, { jobId: string; count: number }>('stopBots');

/** Flip still-pending start targets back to desired 'down'. Bots that already
 *  came up stay up -- cancel is "stop starting", not "stop". */
export const cancelStartAll = DeclareMethod<void, { cancelled: number }>('cancelStartAll');

/** Manually trigger one eager invitation from a running eager bot. `target`
 *  names a muser to invite (resolved case-insensitively); blank lets the bot
 *  pick from the marketplace as its cron would. The outcome is announced in
 *  the bot's console log -- the method only queues. */
export const eagerInvite = DeclareMethod<{ username: string; target?: string }, { queued: boolean }>('eagerInvite');

export type BotMaintenanceAction = 'depleteTix' | 'depleteCards' | 'replenishCards';

/** Manual housekeeping triggers (the BotPage buttons): deposit tickets with
 *  the banker, deposit cards to the depots (buy/shred), or pull cards from a
 *  depot (sell/hybrid) -- immediately, ignoring the cron gates. Outcomes are
 *  announced in the bot's console log; the method only queues. */
export const botMaintenance = DeclareMethod<{ username: string; action: BotMaintenanceAction }, { queued: boolean }>('botMaintenance');

export const validateBot = async (bot: Partial<Bot>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, bot, 'username', { required: true });
  validateString(errors, bot, 'password', { required: true });
  validateString(errors, bot, 'mode', { required: true });
  return errors;
};

export const modelClass: ModelClass<Model, Refs> = {
  collection: Bots,
  editableFields: ['username', 'password', 'mfakey', 'uid', 'mode', 'active', 'status', 'config'],
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return { $or: [{ _id: search }, { username: new RegExp(search, 'gi') }] };
  },
  listMethod: listBots,
  getMethod: getBot,
  createMethod: createBot,
  updateMethod: updateBot,
  deleteMethod: deleteBot,
  pathPrefix: '/bots',
  modelName: 'Bot',
  pluralName: 'Bots',
  validate: validateBot,
};
